import axios from 'axios'
import store from '@/store'
import i18n from '@/i18n'
import { captureLog } from '@/helpers/log'

let http = null
const BASE_URL = '/api/clients'

const getInstance = () => {
	if (http) return http
	const { buildNumber, device_type } = window.appConfig
	const version = `${device_type || 'WEB'}/${buildNumber || 1}/0`
	const token = store.auth.api_token
	store.setApiTokenCookie(token)
	http = axios.create({
		baseURL: BASE_URL,
		headers: {
			Authorization: `Bearer ${token}`,
			'X-Time-Zone': store.timezone,
			'Accept-Language': i18n.global.locale || 'en',
			'X-Version': version,
		},
		paramsSerializer: function (params) {
			return Object.entries(params)
				.map(([key, val]) => {
					return [key, Array.isArray(val) ? val.join(',') : val].join('=')
				})
				.join('&')
		},
	})
	return http
}

const processResponse = (url, resp) => {
	const obj = { ...resp.data, url, fullUrl: resp.request['BS~~U'] }
	if (resp.data.links) {
		Object.entries(resp.data.links)
			.filter(([key, link]) => link)
			.forEach(([key, link]) => {
				let path = link
				if (path.startsWith('http')) {
					const linkUrl = new URL(link)
					path = link.replace(linkUrl.origin + '/clients', BASE_URL)
				}
				if (path.startsWith(BASE_URL)) {
					path = path.replace(BASE_URL, '')
				}
				obj[`${key}Url`] = link
				obj[key] = async () => {
					return processResponse(path, await getInstance().get(path))
				}
			})
	}
	return obj
}

const log = (phase, method, url, data, ...extras) => {
	captureLog('API', phase, `${method} /api/clients/${url}`, data, ...extras)
}

// Success payloads for these endpoints carry live credentials — e.g. the
// single-use Svix portal URL, a bearer credential for the org's webhook
// endpoints and signing secrets, or the integrations bearer JWT. Keep them
// out of the diagnostics log that users export (Ctrl+Opt+L) and hand to
// support.
const CREDENTIAL_RESPONSE_URLS = [
	'users/me/webhooks_portal',
	'organization/integrations/integration_tokens',
]

export const redactCredentials = (url, data) =>
	CREDENTIAL_RESPONSE_URLS.includes(url.split('?')[0].replace(/^\//, ''))
		? '[redacted]'
		: data

export const get = async (url, params, config = {}) => {
	try {
		log('init', 'GET', url, params)
		const resp = await getInstance().get(url, { ...config, params })
		// log the body only — the full axios response carries the
		// Authorization header (#1597)
		log('success', 'GET', url, params, redactCredentials(url, resp.data))
		return processResponse(url, resp)
	} catch (e) {
		log('error', 'GET', url, params, e.message, e)
		throw e
	}
}

export const post = async (url, body, config) => {
	try {
		log('init', 'POST', url, body)
		const resp = await getInstance().post(url, body, config)
		log('success', 'POST', url, body, redactCredentials(url, resp.data.data))
		return resp.data.data
	} catch (e) {
		log('error', 'POST', url, body, e.message, e)
		throw e
	}
}

export const patch = async (url, body, config) => {
	try {
		log('init', 'PATCH', url, body)
		const resp = await getInstance().patch(url, body, config)
		log('success', 'PATCH', url, body, redactCredentials(url, resp.data.data))
		return resp.data.data
	} catch (e) {
		log('error', 'PATCH', url, body, e.message, e)
		throw e
	}
}

export const del = async (url, body, config) => {
	try {
		log('init', 'DELETE', url, body)
		const resp = await getInstance().delete(url, body, config)
		log('success', 'DELETE', url, body, redactCredentials(url, resp.data.data))
		return resp.data.data
	} catch (e) {
		log('error', 'DELETE', url, body, e.message, e)
		throw e
	}
}

export const put = async (url, body, config) => {
	try {
		log('init', 'PUT', url, body)
		const resp = await getInstance().put(url, body, config)
		log('success', 'PUT', url, body, redactCredentials(url, resp.data.data))
		return resp.data.data
	} catch (e) {
		log('error', 'PUT', url, body, e.message, e)
		throw e
	}
}

export const getAllPages = async (url, params = {}) => {
	const data = []
	let resp = await get(url, { 'page[size]': 100, ...params })
	data.push(...resp.data)
	while (resp.next) {
		resp = await resp.next()
		data.push(...resp.data)
	}
	return data
}
